import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminCategoryService, adminProductService } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';

interface DashboardStats {
  categories: number;
  products: number;
  availableProducts: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    categories: 0,
    products: 0,
    availableProducts: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      const [categories, products] = await Promise.all([
        adminCategoryService.getAll(),
        adminProductService.getAll(),
      ]);

      setStats({
        categories: categories.length,
        products: products.length,
        availableProducts: products.filter((p) => p.is_available).length,
      });
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Chargement du dashboard..." />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Categories',
      value: stats.categories,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
      link: '/admin/categories',
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'Produits',
      value: stats.products,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      link: '/admin/products',
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Produits disponibles',
      value: stats.availableProducts,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      link: '/admin/products',
      color: 'from-accent to-accent-light',
    },
  ];

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Dashboard Admin</h1>
          <p className="text-gray-400">Gerez votre plateforme en toute simplicite</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          {statCards.map((card) => (
            <Link
              key={card.title}
              to={card.link}
              className={`card p-6 bg-gradient-to-br ${card.color} border-none hover:scale-105 transition-all duration-300`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm mb-1">{card.title}</p>
                  <p className="text-4xl font-bold text-white">{card.value}</p>
                </div>
                <div className="text-white/80">{card.icon}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Actions rapides</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/admin/categories"
              className="flex items-center space-x-3 p-4 bg-primary-dark/50 rounded-lg hover:bg-primary-dark transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span className="text-white">Nouvelle categorie</span>
            </Link>

            <Link
              to="/admin/products"
              className="flex items-center space-x-3 p-4 bg-primary-dark/50 rounded-lg hover:bg-primary-dark transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span className="text-white">Nouveau produit</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
